import { Alert, Divider, Form, message, Modal, Select, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { getTicketDisplayLabel } from 'features/libra/ticket/ticketDisplayHelpers'
import { useLibraWorkflowSteps } from 'features/libra/workflow/workflowStepsApi'
import { useRolodexEntries } from 'features/rolodex'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'
import { displayNameFromEmail } from 'utils/stringHelpers'

const { Text } = Typography

const ALL_TICKETS_VALUE = '__all_tickets__'
const ALL_WORKFLOWS_VALUE = '__all_workflows__'

interface DeleteTicketsForGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void | Promise<void>
  organizationId: string
  groupId: string
  groupTitle: string
  tickets: ReadonlyArray<LibraTicket>
}

interface FormValues {
  workflowId: string
  ticketIds: ReadonlyArray<string>
}

function normalizeTicketSelection(
  selectedTicketIds: ReadonlyArray<string>,
  previousSelectedTicketIds: ReadonlyArray<string> | undefined
): ReadonlyArray<string> {
  if (!selectedTicketIds.includes(ALL_TICKETS_VALUE)) {
    return selectedTicketIds
  }

  return previousSelectedTicketIds?.includes(ALL_TICKETS_VALUE)
    ? selectedTicketIds.filter((ticketId) => ticketId !== ALL_TICKETS_VALUE)
    : [ALL_TICKETS_VALUE]
}

function getTicketIdsForDeletion({
  tickets,
  groupId,
  workflowId,
  selectedTicketIds,
}: {
  tickets: ReadonlyArray<LibraTicket>
  groupId: string
  workflowId: string
  selectedTicketIds: ReadonlyArray<string>
}): ReadonlyArray<string> {
  const selectedTicketIdSet = new Set(selectedTicketIds)
  const allTicketsSelected = selectedTicketIdSet.has(ALL_TICKETS_VALUE)

  return [
    ...new Set(
      tickets
        .filter(
          (ticket) =>
            ticket.tags[RolodexEntryType.LIBRA_TICKET_GROUP] === groupId &&
            (workflowId === ALL_WORKFLOWS_VALUE ||
              ticket.workflowId === workflowId) &&
            (allTicketsSelected || selectedTicketIdSet.has(ticket.id))
        )
        .map((ticket) => ticket.id)
    ),
  ]
}

function useTicketAssetsQuery({
  candidateAssetIds,
  organizationId,
}: {
  candidateAssetIds: ReadonlyArray<string>
  organizationId: string
}) {
  return useMemo(() => {
    if (candidateAssetIds.length === 0) return undefined

    return {
      type: RolodexEntryType.UNIT,
      ids: [...candidateAssetIds],
      tags: { organization: organizationId },
      limit: candidateAssetIds.length,
      order_by: [
        {
          key: 'name',
          descending: false,
        },
      ],
    }
  }, [candidateAssetIds, organizationId])
}

export const DeleteTicketsForGroupModal: FC<
  DeleteTicketsForGroupModalProps
> = ({
  isOpen,
  onClose,
  onSuccess,
  organizationId,
  groupId,
  groupTitle,
  tickets,
}) => {
  const [form] = Form.useForm<FormValues>()
  const [loading, setLoading] = useState(false)
  const { rolodexBatchDelete } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()

  const groupTickets = useMemo(
    () =>
      tickets.filter(
        (ticket) => ticket.tags[RolodexEntryType.LIBRA_TICKET_GROUP] === groupId
      ),
    [groupId, tickets]
  )

  const workflowIds = useMemo(
    () => [...new Set(groupTickets.map((ticket) => ticket.workflowId))],
    [groupTickets]
  )
  const lockedWorkflowId = workflowIds.length === 1 ? workflowIds[0] : undefined

  const { libraWorkflows, isLoading: areWorkflowsLoading } =
    useLibraWorkflowSteps(
      useMemo(
        () => ({
          ids: workflowIds,
        }),
        [workflowIds]
      )
    )

  const workflowOptions = useMemo(
    () => [
      { label: 'All Workflow Templates', value: ALL_WORKFLOWS_VALUE },
      ...workflowIds.map((workflowId) => ({
        label:
          libraWorkflows.find((workflow) => workflow.id === workflowId)?.name ??
          workflowId,
        value: workflowId,
      })),
    ],
    [libraWorkflows, workflowIds]
  )

  const selectedWorkflowId = Form.useWatch('workflowId', form)
  const selectedTicketIds = Form.useWatch('ticketIds', form)
  const effectiveWorkflowId = lockedWorkflowId ?? selectedWorkflowId

  const candidateTickets = useMemo(
    () =>
      effectiveWorkflowId === ALL_WORKFLOWS_VALUE
        ? groupTickets
        : effectiveWorkflowId
          ? groupTickets.filter(
              (ticket) => ticket.workflowId === effectiveWorkflowId
            )
          : [],
    [effectiveWorkflowId, groupTickets]
  )

  const candidateAssetIds = useMemo(
    () => [
      ...new Set(
        candidateTickets
          .map((ticket) => ticket.tags.unit)
          .filter((assetId): assetId is string => typeof assetId === 'string')
      ),
    ],
    [candidateTickets]
  )

  const ticketAssetsQuery = useTicketAssetsQuery({
    candidateAssetIds,
    organizationId,
  })
  const { units: ticketAssets } = useRolodexEntries(ticketAssetsQuery)

  const ticketOptions = useMemo(() => {
    const unitNames = new Map(
      ticketAssets.map((unit) => [unit.id, unit.data.name || unit.id])
    )
    const workflowsById = new Map(
      libraWorkflows.map((workflow) => [workflow.id, workflow])
    )
    const ticketsByUnit = new Map<string, LibraTicket[]>()

    candidateTickets.forEach((ticket) => {
      const unitId = ticket.tags.unit as string
      const unitTickets = ticketsByUnit.get(unitId)
      if (unitTickets) {
        unitTickets.push(ticket)
      } else {
        ticketsByUnit.set(unitId, [ticket])
      }
    })

    return [
      { label: 'All Tickets', value: ALL_TICKETS_VALUE },
      ...[...ticketsByUnit.entries()].map(([unitId, unitTickets]) => ({
        label: unitNames.get(unitId) ?? unitId,
        options: unitTickets
          .toSorted(
            (a, b) => a.title.localeCompare(b.title) || a.version - b.version
          )
          .map((ticket) => ({
            label: `${getTicketDisplayLabel(
              ticket,
              workflowsById.get(ticket.workflowId)?.lifecycleConfig,
              { includeId: true }
            )} — Workflow: ${
              workflowsById.get(ticket.workflowId)?.name ?? ticket.workflowId
            } — Created by ${displayNameFromEmail(ticket.createdBy)}`,
            value: ticket.id,
          })),
      })),
    ]
  }, [candidateTickets, libraWorkflows, ticketAssets])

  const ticketIdsToDelete = useMemo(
    () =>
      effectiveWorkflowId
        ? getTicketIdsForDeletion({
            tickets: groupTickets,
            groupId,
            workflowId: effectiveWorkflowId,
            selectedTicketIds: selectedTicketIds ?? [],
          })
        : [],
    [effectiveWorkflowId, groupId, groupTickets, selectedTicketIds]
  )

  const selectedAssetCount = useMemo(() => {
    if (!effectiveWorkflowId) return 0
    const selectedIds = new Set(ticketIdsToDelete)
    return new Set(
      groupTickets
        .filter(
          (ticket) =>
            typeof ticket.tags.unit === 'string' && selectedIds.has(ticket.id)
        )
        .map((ticket) => ticket.tags.unit as string)
    ).size
  }, [effectiveWorkflowId, groupTickets, ticketIdsToDelete])

  const selectedWorkflowCount = useMemo(() => {
    const selectedIds = new Set(ticketIdsToDelete)
    return new Set(
      groupTickets
        .filter((ticket) => selectedIds.has(ticket.id))
        .map((ticket) => ticket.workflowId)
    ).size
  }, [groupTickets, ticketIdsToDelete])

  const resetTicketSelection = useCallback(() => {
    form.setFieldValue('ticketIds', [])
  }, [form])

  const resetModal = useCallback(() => {
    form.resetFields()
  }, [form])

  const handleSubmit = async (values: FormValues) => {
    const workflowId = lockedWorkflowId ?? values.workflowId
    const ids = getTicketIdsForDeletion({
      tickets: groupTickets,
      groupId,
      workflowId,
      selectedTicketIds: values.ticketIds,
    })

    if (
      (workflowId !== ALL_WORKFLOWS_VALUE &&
        !workflowIds.includes(workflowId)) ||
      ids.length === 0
    ) {
      messageApi.error('No matching tickets were found for deletion')
      return
    }

    setLoading(true)
    try {
      await rolodexBatchDelete(ids, true)
      messageApi.success(
        `${ids.length} ticket${ids.length > 1 ? 's' : ''} deleted successfully!`
      )
      resetModal()
      onClose()

      try {
        await onSuccess()
      } catch (refreshError) {
        console.warn(
          'Tickets were deleted, but the dashboard refresh failed',
          refreshError
        )
      }
    } catch (error) {
      console.error('Error deleting tickets:', error)
      messageApi.error('Failed to delete tickets. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    if (loading) return
    resetModal()
    onClose()
  }

  const lockedWorkflow = lockedWorkflowId
    ? libraWorkflows.find((workflow) => workflow.id === lockedWorkflowId)
    : undefined

  return (
    <>
      {contextHolder}
      <Modal
        title={`Delete Tickets for "${groupTitle}"`}
        open={isOpen}
        onOk={form.submit}
        onCancel={handleCancel}
        afterClose={resetModal}
        confirmLoading={loading}
        okText='Delete Tickets'
        cancelText='Cancel'
        okButtonProps={{ danger: true }}
        cancelButtonProps={{ disabled: loading }}
        closable={!loading}
        keyboard={!loading}
        maskClosable={!loading}
        width={700}>
        <Form
          form={form}
          layout='vertical'
          initialValues={{ workflowId: ALL_WORKFLOWS_VALUE }}
          onFinish={handleSubmit}>
          {lockedWorkflowId ? (
            <Form.Item label='Workflow Template'>
              <Text>
                {areWorkflowsLoading
                  ? 'Loading…'
                  : (lockedWorkflow?.name ?? lockedWorkflowId)}
              </Text>
            </Form.Item>
          ) : (
            <Form.Item
              name='workflowId'
              label='Workflow Template'
              rules={[{ required: true, message: 'Please select a workflow' }]}>
              <Select
                showSearch
                placeholder='Select workflow'
                loading={areWorkflowsLoading}
                options={workflowOptions}
                optionFilterProp='label'
                onChange={resetTicketSelection}
              />
            </Form.Item>
          )}

          <Divider />

          <Form.Item
            name='ticketIds'
            normalize={normalizeTicketSelection}
            label={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}>
                <span>Tickets</span>
                <Text
                  type='secondary'
                  style={{ fontSize: '12px', fontWeight: 'normal' }}>
                  Choose exact tickets by asset, or select All Tickets. Search
                  by ticket, workflow, or creator. Tickets from other ticket
                  groups are not shown.
                </Text>
              </div>
            }
            rules={[
              { required: true, message: 'Please select at least one ticket' },
            ]}>
            <Select
              mode='multiple'
              showSearch
              disabled={candidateTickets.length === 0}
              placeholder='Select tickets to delete...'
              options={ticketOptions}
              optionFilterProp='label'
              notFoundContent={
                effectiveWorkflowId
                  ? 'No matching tickets found'
                  : 'Select a workflow first'
              }
              maxTagCount='responsive'
            />
          </Form.Item>

          {ticketIdsToDelete.length > 0 && (
            <Alert
              type='warning'
              showIcon
              message={`${ticketIdsToDelete.length} ticket${ticketIdsToDelete.length > 1 ? 's' : ''} selected for deletion`}
              description={`Only the selected ticket${ticketIdsToDelete.length > 1 ? 's' : ''} in "${groupTitle}" will be removed across ${selectedAssetCount} asset${selectedAssetCount !== 1 ? 's' : ''} and ${selectedWorkflowCount} workflow template${selectedWorkflowCount !== 1 ? 's' : ''}. Tickets for these assets in other ticket groups will not be affected.`}
            />
          )}
        </Form>
      </Modal>
    </>
  )
}
